import { Cpu, Megaphone, CalendarClock, PhoneCall, FileCheck2, LineChart } from "lucide-react";

export const SERVICES = [
  {
    icon: Cpu,
    title: "Runs your operations",
    short: "Your day-to-day ops, handled end to end.",
    desc: "Your right hand runs the moving parts of your business, keeping every process on track so nothing falls through while you focus on growth.",
  },
  {
    icon: Cpu,
    title: "Masters your AI tools",
    short: "Your stack, operated by an expert.",
    desc: "From your CRM to your automations, your operator wields the AI tools you already pay for and makes them actually work for you.",
  },
  {
    icon: Megaphone,
    title: "Drives your marketing",
    short: "Content, listings, and campaigns kept moving.",
    desc: "Social posts, listing promotion, email and follow-up campaigns executed consistently so your pipeline never goes quiet.",
  },
  {
    icon: CalendarClock,
    title: "Owns your calendar",
    short: "Your time, protected and organized.",
    desc: "Scheduling, reminders, and prep handled for you, so you walk into every appointment ready and never double-booked.",
  },
  {
    icon: PhoneCall,
    title: "Works your leads & agents",
    short: "Speed-to-lead that books appointments.",
    desc: "Your operator responds to leads, talks to agents, qualifies, and books appointments straight onto your calendar.",
  },
  {
    icon: FileCheck2,
    title: "Closes your deals",
    short: "Contract to close, every deadline tracked.",
    desc: "Transaction coordination, disclosures, and multi-party coordination driven to the finish line, on time, every time.",
  },
];

export const STEPS = [
  { n: "01", title: "Discovery call", desc: "30 minutes, we learn your business, your tools, and where your time leaks today." },
  { n: "02", title: "Matched & trained", desc: "Your dedicated right hand is set up on your stack and trained on how you work." },
  { n: "03", title: "They run it", desc: "Ops, marketing, calendar, leads, and deals move quietly in the background, reviewed by a human." },
  { n: "04", title: "You grow", desc: "You spend your hours on the one thing that matters: growing your business." },
];

export const STATS = [
  { num: 20, suffix: "+", label: "hours handed back to you every week" },
  { num: 3, suffix: "x", label: "faster speed-to-lead on average" },
  { num: 100, suffix: "%", label: "visibility into every active file" },
  { num: 0, suffix: "", label: "missed deadlines on our watch" },
];

// The Quexify difference — five pillars (original, outcome-led positioning)
export const PILLARS = [
  {
    k: "01",
    title: "Real-estate fluency",
    desc: "Your right hand already speaks contracts, contingencies, disclosures and MLS. No training you from scratch, no explaining the basics of a deal.",
  },
  {
    k: "02",
    title: "One partner, whole back office",
    desc: "Operations, marketing, leads, calendar and closings run under one roof, by one dedicated operator, so nothing lives in a gap between tools or people.",
  },
  {
    k: "03",
    title: "Sharper every week",
    desc: "The longer we run your business, the more your right hand learns your playbook, encoding your best moves into repeatable systems that compound.",
  },
  {
    k: "04",
    title: "Paid on outcomes",
    desc: "You don't buy hours or headcount. We shape the engagement around the result, deals closed, hours returned, so our incentive is your growth.",
  },
  {
    k: "05",
    title: "Human-checked, always",
    desc: "AI does the heavy lifting; a real operator owns the judgment. Every message, deadline and hand-off is reviewed by a person before it reaches a client.",
  },
];

// Signature philosophy — the evolution beyond the old VA / TC model (original)
export const MANIFESTO = {
  eyebrow: "The Quexify standard",
  heading: "The virtual assistant era is over.",
  lead: "The old model sold you cheaper hours. We refused to build a discount version of the past.",
  tenets: [
    { verb: "Unlearn", line: "the busywork model. Hours on a timesheet were never the point, the outcome was." },
    { verb: "Unlock", line: "your best hours by handing everything that isn't selling to an operator built for it." },
    { verb: "Unleash", line: "a business that runs without living in your head, and grows because you finally have room to grow it." },
  ],
};

// Who we serve
export const SERVE = [
  { title: "Solo agents", line: "Reclaim your evenings and scale without hiring." },
  { title: "Growing teams", line: "Add capacity and consistency without adding payroll." },
  { title: "Brokerages", line: "Give every agent the same reliable operational spine." },
  { title: "Property managers", line: "Keep coordination, renewals and owners perfectly in sync." },
  { title: "Loan officers", line: "Move files faster with proactive, deadline-driven follow-up." },
];

// Richer proof band — an outcome + the anonymized business it came from,
// each deep-linking to its matching case study.
export const PROOF = [
  { outcome: "22 hrs / week reclaimed", who: "Solo agent, Georgia", slug: "solo-agent-georgia" },
  { outcome: "Zero missed deadlines in 12 months", who: "Boutique brokerage, North Carolina", slug: "boutique-brokerage-north-carolina" },
  { outcome: "$180k of agent time returned / yr", who: "Growing team, Maryland", slug: "growing-team-maryland" },
  { outcome: "First full weekend off in 3 years", who: "Independent agent, Georgia", slug: "independent-agent-georgia" },
];

// Bold two-part statement (original) — the emotional core of the pitch
export const STATEMENT = {
  lineTop: "Ambition is everywhere.",
  lineBottom: "Bandwidth isn't.",
  body: "Every agent has more they could be doing, more leads to work, more listings to push, more relationships to build, and no more hours to do it in. Quexify is the bandwidth: an AI-augmented right hand that runs everything but the selling, and stands behind the outcome.",
};



export const MODELS = [
  {
    tag: "For solo agents",
    title: "Per deal",
    price: "Pay as you close",
    line: "You only invest when a deal is live. We run it to close, you keep your margin.",
    features: ["Contract-to-close coordination", "Deadline & disclosure tracking", "No monthly commitment"],
  },
  {
    tag: "For growing teams",
    title: "Embedded right hand",
    price: "Fixed monthly",
    line: "A dedicated operator inside your business, matched to your tools and your rhythm.",
    features: ["Ops, calendar & leads", "AI tools operated for you", "Marketing kept in motion", "Priority direct access"],
    featured: true,
  },
  {
    tag: "For brokerages",
    title: "Outcome based",
    price: "You win, we win",
    line: "We price against the results that matter, faster closings, fewer fall-throughs, hours returned.",
    features: ["Multi-agent coverage", "Custom automations", "Dedicated success lead", "Quarterly business reviews"],
  },
];

export const GUARANTEES = [
  "30-day risk-free guarantee", "Free replacement if the fit isn't right", "Month to month, no lock-in",
];

export const FAQS = [
  { q: "Is this just a transaction coordinator?", a: "No. Transaction coordination is one thing your right hand does. They also run your operations, operate your AI tools, drive your marketing, own your calendar, work your leads, talk to agents, and book appointments, so you can focus on growth." },
  { q: "Do I lose control of my business?", a: "Never. You get full weekly visibility and direct access to your operator any time something needs your call. They work the way you work." },
  { q: "How fast can my right hand start?", a: "Typically within days of your discovery call, once we understand your tools and process." },
  { q: "Which tools do you work with?", a: "We plug into the stack you already use, your CRM, calendar, marketing and automation tools. No rip and replace." },
  { q: "Is there a contract or lock-in?", a: "Month to month. We'd rather earn it every month than lock you in, backed by a 30-day risk-free guarantee." },
  { q: "Is this only for real estate?", a: "We're built for real estate agents, teams and brokerages first, and the same right-hand model scales to any relationship-driven business that runs on deals." },
];

export const CALC_TASKS = [
  { key: "admin", label: "Admin & operations", defaultHours: 8 },
  { key: "leads", label: "Lead follow-up & calls", defaultHours: 6 },
  { key: "marketing", label: "Marketing & content", defaultHours: 4 },
  { key: "coordination", label: "Deal & transaction coordination", defaultHours: 5 },
  { key: "scheduling", label: "Scheduling & calendar", defaultHours: 3 },
];

// Tools we operate inside for clients — real names + official brand logos
// (self-hosted SVGs in /public/logos). Shown as an "and beyond" strip.
export const TOOLS = [
  // AI
  { name: "OpenAI", logo: "/logos/openai.svg" },
  { name: "Claude", logo: "/logos/claude.svg" },
  { name: "Gemini", logo: "/logos/gemini.svg" },
  // Automation / no-code
  { name: "Zapier", logo: "/logos/zapier.svg" },
  { name: "Make", logo: "/logos/make.svg" },
  { name: "n8n", logo: "/logos/n8n.svg" },
  { name: "Airtable", logo: "/logos/airtable.svg" },
  { name: "Notion", logo: "/logos/notion.svg" },
  // CRM / sales
  { name: "HubSpot", logo: "/logos/hubspot.svg" },
  { name: "Salesforce", logo: "/logos/salesforce.svg" },
  // Comms & scheduling
  { name: "Slack", logo: "/logos/slack.svg" },
  { name: "Twilio", logo: "/logos/twilio.svg" },
  { name: "Calendly", logo: "/logos/calendly.svg" },
  { name: "Zoom", logo: "/logos/zoom.svg" },
  // Docs
  { name: "Google Drive", logo: "/logos/googledrive.svg" },
  // Marketing & social
  { name: "Mailchimp", logo: "/logos/mailchimp.svg" },
  { name: "Canva", logo: "/logos/canva.svg" },
  { name: "Meta", logo: "/logos/meta.svg" },
  { name: "Instagram", logo: "/logos/instagram.svg" },
  { name: "LinkedIn", logo: "/logos/linkedin.svg" },
  { name: "YouTube", logo: "/logos/youtube.svg" },
  // Payments
  { name: "Stripe", logo: "/logos/stripe.svg" },
];
